import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IFilterCount } from 'components/shared/AppTable/components/TableFilters/TableFilters';
import { ViewType } from 'enum/ViewTypes';
import {
  IEntity, IView, IViewDetail, IViewsResponse,
} from 'interfaces/IView';

export interface IViewsState {
  views: IView[];
  userViews: IView[];
  totalCount: number | null;
  userTotalCount: number | null;
  filters: IFilterCount | null;
  userFilters: IFilterCount | null;
  isViewsLoading: boolean | null;
  triggerViews: object;
  detailViews: IViewsResponse | null;
  editViews: IViewsResponse | null;
  viewDetail: IViewDetail | null;
  tableView: IViewDetail | null;
  detailView: IViewDetail | null;
  editView: IViewDetail | null;
  favoriteViews: IView[];
  isShowFormatterModal: boolean;
  viewData: IEntity[];
  isViewDataLoading: boolean | null;
  isViewDataError: boolean;
  totalCountViewItems: number | null;
  viewDataDetails: any;
  isViewNotFound?: boolean;
}

export const initialState: IViewsState = {
  views: [],
  userViews: [],
  totalCount: null,
  userTotalCount: null,
  filters: null,
  userFilters: null,
  isViewsLoading: null,
  triggerViews: {},
  detailViews: null,
  editViews: null,
  viewDetail: null,
  tableView: null,
  detailView: null,
  editView: null,
  favoriteViews: [],
  isShowFormatterModal: false,
  viewData: [],
  viewDataDetails: null,
  isViewDataLoading: null,
  totalCountViewItems: null,
  isViewDataError: false,
  isViewNotFound: false,
};

export const viewsSlice = createSlice({
  name: 'views',
  initialState,
  reducers: {
    setViews(state, action: PayloadAction<IView[]>) {
      state.views = action.payload;
    },
    setUserViews(state, action: PayloadAction<IView[]>) {
      state.userViews = action.payload;
    },
    setTotalCount(state, action: PayloadAction<null | number>) {
      state.totalCount = action.payload;
    },
    setUserTotalCount(state, action: PayloadAction<null | number>) {
      state.userTotalCount = action.payload;
    },
    setFilters(state, action: PayloadAction<IFilterCount | null>) {
      state.filters = action.payload;
    },
    setUserFilters(state, action: PayloadAction<IFilterCount | null>) {
      state.userFilters = action.payload;
    },
    setIsViewsLoading(state, action: PayloadAction<boolean | null>) {
      state.isViewsLoading = action.payload;
    },
    setDetailViews(state, action: PayloadAction<IViewsResponse | null>) {
      state.detailViews = action.payload;
    },
    setEditViews(state, action: PayloadAction<IViewsResponse | null>) {
      state.editViews = action.payload;
    },
    setViewDetail(state, action: PayloadAction<IViewDetail | null>) {
      state.viewDetail = action.payload;
    },
    setTableView(state, action: PayloadAction<IViewDetail | null>) {
      state.tableView = action.payload;
    },
    setDetailView(state, action: PayloadAction<IViewDetail | null>) {
      state.detailView = action.payload;
    },
    setEditView(state, action: PayloadAction<IViewDetail | null>) {
      state.editView = action.payload;
    },
    setIsShowFormatterModal(state, action: PayloadAction<boolean>) {
      state.isShowFormatterModal = action.payload;
    },
    setIsViewNotFound(state, action: PayloadAction<boolean>) {
      state.isViewNotFound = action.payload;
    },
    setTriggerViews(state) {
      state.triggerViews = {};
    },
    setFavoriteViews(state, action: PayloadAction<IView[]>) {
      state.favoriteViews = action.payload;
    },
    setViewData(state, action: PayloadAction<IEntity[]>) {
      state.viewData = action.payload;
    },
    setIsViewDataError(state, action: PayloadAction<boolean>) {
      state.isViewDataError = action.payload;
    },
    setViewDataDetails(state, action: PayloadAction<any>) {
      state.viewDataDetails = action.payload;
    },
    setIsViewDataLoading(state, action: PayloadAction<boolean | null>) {
      state.isViewDataLoading = action.payload;
    },
    setTotalCountViewItems(state, action: PayloadAction<null | number>) {
      state.totalCountViewItems = action.payload;
    },
    resetViews: (state, action: PayloadAction<ViewType[] | null>) => {
      const viewsToReset = action.payload;

      if (!viewsToReset) {
        state.tableView = null;
        state.editView = null;
        state.detailView = null;
      } else {
        viewsToReset.forEach((view) => {
          state[`${view.toLowerCase()}View`] = null;
        });
      }

      state.isViewDataLoading = null;
      state.isViewsLoading = null;
    },
    resetViewsState: () => initialState,
  },
});

export const {
  setTotalCount,
  setFilters,
  setViews,
  setUserViews,
  setIsViewsLoading,
  setTriggerViews,
  setDetailViews,
  setEditViews,
  setViewDetail,
  setTableView,
  setEditView,
  setDetailView,
  setIsViewDataError,
  setIsViewNotFound,
  setFavoriteViews,
  setIsShowFormatterModal,
  setViewData,
  setIsViewDataLoading,
  setTotalCountViewItems,
  setUserTotalCount,
  setUserFilters,
  setViewDataDetails,
  resetViewsState,
  resetViews,
} = viewsSlice.actions;

export default viewsSlice.reducer;
